/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  TreeSelect,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { areaBuscar, normalizeData } from 'utils/normalizers';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import {
  getEstadosGlobales,
  getAreas,
  getContentTypesUR,
} from 'api/catalogos/index';
import {
  getSolicitudesAreas,
  postSolicitudArea,
  deleteSolicitudArea,
  patchSolicitudArea,
  getSolicitudArea,
} from 'api/nominas/solicitudes/solicitudesAreas';
import { checkToken } from 'api/utils';

export const solicitudAreaPermission = {
  permissionModel: 'solicitudarea',
  catalogsModels: [],
  tabsModels: [],
};

const SolcitudesAreas = ({ permission, selected }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [areas, setAreas] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getSolicitudesAreas({ solicitud: selected });
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _areas,
        _contentTypes,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getAreas(),
        getContentTypesUR(),
        getSolicitudesAreas({ solicitud: selected }),
      ]);
      const formattedURS = normalizeData(_areas);
      if (mounted) {
        setEstados(_estados);
        setAreas(formattedURS);
        setContentTypes(_contentTypes);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
  };

  // eslint-disable-next-line consistent-return
  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _area = await getSolicitudArea(match.id);

    const area = areaBuscar(_area.area_content_id, areas);
    _area.unidad_responsable = area?.value;

    setTimeout(() => {
      if (_area) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_area));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const getURValuesToSend = () => {
    const unidad_responsable = form.getFieldValue('unidad_responsable');
    if (unidad_responsable) {
      const [area, _nivel] = unidad_responsable.split('/');
      const area_content_id = contentTypes.find((ct) => ct.nivel.toString() === _nivel).id;
      return { area, area_content_id };
    }
    return { area: undefined, area_content_id: undefined };
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const contentValues = getURValuesToSend();
      const values = {
        ...form.getFieldsValue(),
        area: contentValues?.area_content_id,
        area_content_id: contentValues?.area,
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchSolicitudArea(key, { ...values, solicitud: selected }) : postSolicitudArea({ ...values, solicitud: selected }));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteSolicitudArea(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Área',
      dataIndex: 'area_content_id',
      key: 'area_content_id',
      render: (val) => areaBuscar(val, areas)?.tableTitle,
      width: 200,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    contratacion: [requiredRule],
    estatus: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Solicitud Parámetro`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
            )}
          onCancel={onCancel}
          forceRender
          width="35%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="unidad_responsable"
                label="Área"
                rules={rules.required}
                hasFeedback
              >
                <TreeSelect
                  showSearch
                  treeNodeFilterProp="title"
                  style={{ width: '100%' }}
                  dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                  treeData={areas}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.estatus}
                hasFeedback
                initialValue={1}
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  disabled={selectedRowKeys.length <= 0}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Solicitud Parámetro ${areaBuscar(form.getFieldValue('area_content_id'), areas)?.tableTitle}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default SolcitudesAreas;
